import { Descriptions, Empty, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '@/hooks/use-user-profile/use-user-profile';
import { UserCallHistoryEntry, UserProfileData } from '@/services/user-profile-service/user-profile.service.types';
import { CONVERSATION_TYPES } from '@/services/webchat-service/webchat.service.types';
import { getConversationChannel } from '@/utils/conversation-channel';
import styles from './user-profile-panel.module.scss';

type Props = {
  userId: number;
};

// Renders the linked User's profile + recent conversation history. Only
// instantiated when the parent has confirmed `userId` is set — the actions
// sidebar hides the entire tab when the caller is anonymous.
export function UserProfilePanel({ userId }: Props) {
  const { t } = useTranslation('webchat');
  const { user, isLoadingUser, profileData, callHistory, isError } = useUserProfile(userId);

  if (isLoadingUser) {
    return (
      <div className={styles.center}>
        <Spin />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <Empty description={t('userProfile.loadError')} />
    );
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name || '—';
  const profileEntries = flattenProfileData(profileData);
  const callsCount = callHistory?.calls_count ?? 0;
  const lastContact = callHistory?.last_contact;

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <div className={styles.name} data-testid="user-profile-name">{fullName}</div>
        <Tag color={callsCount > 1 ? 'blue' : 'default'}>
          {callsCount > 1 ? t('userProfile.priorContacts', { count: callsCount }) : t('userProfile.firstContact')}
        </Tag>
      </header>

      <Descriptions size="small" column={1} className={styles.section}>
        {user.phone && <Descriptions.Item label={t('fields.phone')}>{user.phone}</Descriptions.Item>}
        {user.email && <Descriptions.Item label={t('userProfile.email')}>{user.email}</Descriptions.Item>}
        {lastContact && (
          <Descriptions.Item label={t('userProfile.lastContact')}>
            {dayjs(lastContact).format('D MMM YYYY · HH:mm')}
          </Descriptions.Item>
        )}
      </Descriptions>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>{t('userProfile.accountInfo')}</div>
        {profileEntries.length === 0 ? (
          <div className={styles.muted}>{t('userProfile.noProfileDocument')}</div>
        ) : (
          <Descriptions size="small" column={1} bordered>
            {profileEntries.map(([k, v]) => (
              <Descriptions.Item key={k} label={k}>{String(v)}</Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>{t('userProfile.recentConversations')}</div>
        {callHistory && callHistory.history_calls.length > 0 ? (
          <ul className={styles.calls}>
            {callHistory.history_calls.slice(0, 5).map((call) => (
              <CallRow key={call.id} call={call} />
            ))}
          </ul>
        ) : (
          <div className={styles.muted}>{t('userProfile.noPriorConversations')}</div>
        )}
      </section>
    </div>
  );
}

function CallRow({ call }: { call: UserCallHistoryEntry }) {
  return (
    <li className={styles.call}>
      <div className={styles.callMeta}>
        <span>{dayjs(call.created_at).format('D MMM · HH:mm')}</span>
        {call.type && <Tag color="default">{prettyType(call.type)}</Tag>}
        {call.language && <Tag>{call.language}</Tag>}
      </div>
      {call.convo_summary && <div className={styles.callSummary}>{call.convo_summary}</div>}
    </li>
  );
}

// Profile documents are tenant-shaped (sometimes a flat record, sometimes an
// array of records, sometimes nested). Rather than try to be clever, flatten
// the first object we find into `[key, scalarValue][]` pairs and skip the
// rest. The sidebar is a quick-glance UI, not a full record viewer.
function flattenProfileData(data: UserProfileData | undefined): Array<[string, unknown]> {
  if (!data) return [];
  const record = Array.isArray(data) ? data[0] : data;
  if (!record || typeof record !== 'object') return [];
  return Object.entries(record).filter(([, v]) => v != null && (typeof v !== 'object' || Array.isArray(v) === false));
}

// Known conversation types get the same label as everywhere else; an unknown type is shown raw.
function prettyType(type: string): string {
  return (CONVERSATION_TYPES as readonly string[]).includes(type) ? getConversationChannel(type).label : type;
}
